import { discountedPlan, orderLink } from '../config/promo';

/**
 * Plan catalogue. The list prices below are the undiscounted reference prices;
 * the active promotion is applied at the end of this file via `discountedPlan`,
 * which also rewrites each order link because the checkout endpoint encodes the
 * price in its id (`/order.php?id=<price>`).
 */

/** Features shared by every plan, listed once instead of repeated per plan. */
const BASE_FEATURES = [
    '+79.000 chaînes, +249.000 films et séries',
    'Netflix, Disney+, Amazon Prime Video, HBO Max, OCS, Paramount+, Apple TV+, Discovery+',
    'Ligue 1+ et sport français en FULL HD / 4K',
    'Choix illimité de films et séries',
    'Qualité 4K / FULL HD / HD / SD',
    'Catch-up et guide des programmes (EPG)',
    'Serveurs rapides et stables',
    'Compatible avec tous les appareils',
    'Contenu régulièrement mis à jour',
];

const ADULT_FEATURE = 'Chaînes et films pour adultes 🔞';
const GUARANTEE_15 = '15 jours garantie de remboursement';

const screensFeature = (count) =>
    count === 2 ? 'Deux écrans actifs' : count === 3 ? 'Trois écrans actifs' : 'Quatre écrans actifs';

const RAW_PLAN_GROUPS = {
    standard: {
        label: 'Standard',
        plans: [
            {
                id: 'base',
                name: 'Forfait de base',
                price: '29.00',
                duration: '15 mois',
                screens: 1,
                features: [...BASE_FEATURES, GUARANTEE_15],
            },
            {
                id: 'premium',
                name: 'Forfait Premium',
                price: '39.00',
                duration: '24 mois',
                screens: 1,
                isPopular: true,
                badge: 'Le plus populaire',
                features: [ADULT_FEATURE, ...BASE_FEATURES, GUARANTEE_15],
            },
            {
                id: 'gold',
                name: 'Forfait Gold',
                price: '49.00',
                duration: '18 mois',
                screens: 2,
                badge: 'Multi-écrans',
                features: [screensFeature(2), ADULT_FEATURE, ...BASE_FEATURES, GUARANTEE_15],
            },
        ],
    },
    multiScreen: {
        label: 'Multi-écrans',
        plans: [
            {
                id: 'or',
                name: 'Forfait Or',
                price: '59.00',
                duration: '24 mois',
                screens: 2,
                features: [screensFeature(2), ...BASE_FEATURES, GUARANTEE_15],
            },
            {
                id: 'platinum',
                name: 'Forfait Platinum',
                price: '69.00',
                duration: '18 mois',
                screens: 3,
                isPopular: true,
                badge: 'Le plus populaire',
                features: [screensFeature(3), ADULT_FEATURE, ...BASE_FEATURES, GUARANTEE_15],
            },
            {
                id: 'diamant',
                name: 'Forfait Diamant',
                price: '89.00',
                duration: '18 mois',
                screens: 4,
                badge: 'Le plus complet',
                features: [screensFeature(4), ADULT_FEATURE, ...BASE_FEATURES, GUARANTEE_15],
            },
        ],
    },
};

const RAW_LIFETIME_PLAN = {
    id: 'lifetime',
    name: 'Forfait à vie',
    price: '129.00',
    duration: 'accès permanent',
    screens: 2,
    features: [
        'Tous les avantages Premium + Gold',
        'Accès illimité à vie',
        'Mises à jour gratuites à vie',
        'Support prioritaire',
        '30 jours garantie de remboursement',
    ],
};

/* --------------------------------------------------------------------------
 * Order links are derived from the price rather than written by hand, so the
 * checkout id can never drift from the amount shown on the card. The base URL
 * comes from REACT_APP_ORDER_URL in .env.
 *
 * `discountedPlan` then applies the active promotion (currently a no-op — see
 * src/config/promo.js).
 * ----------------------------------------------------------------------- */

const prepare = (plan) => discountedPlan({ ...plan, link: orderLink(plan.price) });

export const PLAN_GROUPS = Object.fromEntries(
    Object.entries(RAW_PLAN_GROUPS).map(([key, group]) => [
        key,
        { ...group, plans: group.plans.map(prepare) },
    ]),
);

export const LIFETIME_PLAN = prepare(RAW_LIFETIME_PLAN);

/** Every plan flattened, for structured data and analytics. */
export const ALL_PLANS = [
    ...PLAN_GROUPS.standard.plans,
    ...PLAN_GROUPS.multiScreen.plans,
    LIFETIME_PLAN,
];
